import { Emitter, type Unsubscribe } from './emitter';
import type { FieldGroupEvents } from './field-group';
import type { InstallmentsError, InstallmentsResult, InstallmentsSource } from './installments';
import type { SiteStatusGate } from './site-status';

/** The slice of `FieldGroup` the feed listens to. */
export interface FieldInputSource {
  on(event: 'fieldInput', callback: FieldGroupEvents['fieldInput']): Unsubscribe;
}

interface FeedEvents {
  result: (result: InstallmentsResult | null) => void;
  error: (error: InstallmentsError) => void;
}

const FULL_BIN = /^\d{8}$/;

/**
 * Turns the PAN field's BIN stream (the existing `field-input` relay, no new
 * wire message) into `installments`/`installmentsError` events for one PAN
 * handle. It only listens while at least one `installments` listener is
 * registered, so merchants that never subscribe pay no network cost.
 *
 * One request per new 8-digit BIN, no retries. A BIN change aborts the
 * in-flight request and bumps `seq`, so a stale response can never emit even
 * if the abort lost the race. When the BIN an emitted result belongs to goes
 * away (shorter, cleared or replaced), `null` is emitted so the page can
 * clear its selector.
 */
export class InstallmentsFeed {
  private readonly events = new Emitter<FeedEvents>();
  private listeners = 0;
  private detachInput: Unsubscribe | undefined;
  private inflight: AbortController | undefined;
  private seq = 0;
  private requestedBin: string | undefined;
  private emittedBin: string | undefined;

  constructor(
    private readonly group: FieldInputSource,
    private readonly token: string,
    private readonly source: InstallmentsSource,
    private readonly siteStatus?: SiteStatusGate,
  ) {}

  onResult(callback: FeedEvents['result']): Unsubscribe {
    if (!this.source.enabled) {
      this.source.warnDisabledOnce();
      return () => {};
    }
    const off = this.events.on('result', callback);
    if (++this.listeners === 1) {
      this.detachInput = this.group.on('fieldInput', (field, _length, bin) => {
        if (field === 'pan') this.onBin(bin);
      });
    }
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      off();
      if (--this.listeners === 0) {
        this.detachInput?.();
        this.detachInput = undefined;
        this.reset();
      }
    };
  }

  onError(callback: FeedEvents['error']): Unsubscribe {
    return this.events.on('error', callback);
  }

  /** Aborts any in-flight request and forgets the BIN state without
   * emitting. Called on unmount/destroy. */
  reset(): void {
    this.cancel();
    this.requestedBin = undefined;
    this.emittedBin = undefined;
  }

  private cancel(): void {
    this.seq++;
    this.inflight?.abort();
    this.inflight = undefined;
  }

  private onBin(bin: string | undefined): void {
    const next = bin !== undefined && FULL_BIN.test(bin) ? bin : undefined;
    if (next === this.requestedBin) return;
    this.cancel();
    this.requestedBin = next;
    if (this.emittedBin !== undefined) {
      this.emittedBin = undefined;
      this.events.emit('result', null);
    }
    if (next === undefined || this.siteStatus?.peek()?.status === 'not_authorized') return;
    void this.request(next);
  }

  private async request(bin: string): Promise<void> {
    const seq = this.seq;
    const controller = new AbortController();
    this.inflight = controller;
    const outcome = await this.source.request(this.token, bin, controller.signal);
    if (seq !== this.seq) return;
    this.inflight = undefined;
    if (outcome.status === 'ok') {
      this.emittedBin = bin;
      this.events.emit('result', outcome.result);
    } else if (outcome.status === 'error') {
      this.events.emit('error', outcome.error);
    }
  }
}
